import { useEffect, type RefObject } from 'react'

/**
 * Shared sticky-pin scaffold (#1104): the arming machinery `useHeroSticky`
 * and `useRefineSticky` duplicated — the desktop matchMedia gate (with the
 * deprecated addListener fallback for older embedded webviews), the sentinel
 * IntersectionObserver with the queued-notification guard (#1092 review r3:
 * `disconnect()` does not clear already-queued IO callbacks), and the
 * setup/teardown/media-change plumbing. The hooks keep their own state
 * machines (what entering/leaving the pinned state MEANS diverges); this
 * module owns only when those transitions fire.
 */

/** The SBP desktop/mobile cutover — the toolkit's real functional breakpoint,
 *  not 768 or 960. Kept in sync with the literals in explorer.css (CSS cannot
 *  import; grep `min-width: 933px` / `max-width: 932px` there) and with the
 *  non-sticky hero preload hint in widget-core's utils/preload-hints.ts
 *  (`(100vw - 932px)`, #1742). */
export const DESKTOP_MIN_QUERY = '(min-width: 933px)'
export const NARROW_QUERY = '(max-width: 932px)'

export interface StickyPinCallbacks {
  /** The sentinel scrolled above the pin boundary — enter the pinned state.
   *  Must be referentially stable (useCallback). */
  onEnter: () => void
  /** The sentinel is back in view — leave the pinned state. Also called on
   *  teardown (unmount or mobile media flip). Stable. */
  onExit: () => void
  /** Runs right after the observer arms — attach listeners that live only
   *  while armed (scroll/resize handlers, ResizeObservers). Stable. */
  onArm?: () => void
  /** Mirror of onArm, runs during teardown BEFORE onExit. Stable. */
  onDisarm?: () => void
  /**
   * Offset-aware pin boundary (#1220 item 3): the pinned surface renders
   * below a configured host-header offset (`search.heroStickyTopPx`), so the
   * pin must fire when the sentinel crosses THAT line, not the viewport top —
   * otherwise the bar slides under the host header first and then pops down.
   * Implemented as a negative-top rootMargin plus the matching direction
   * check. 0 (the default) keeps the observer byte-identical to the
   * pre-#1104 hooks: no rootMargin, boundary at the viewport top. The 64px
   * compression threshold is deliberately NOT this number — it stays a
   * scroll-distance measure in useRefineSticky.
   */
  pinBoundaryPx?: number
}

/**
 * Arms the observer on the caller-owned `sentinelRef` while `enabled` and
 * the desktop query matches; sets `armedRef` true/false as the listeners arm
 * and disarm (the callers' async retries and ResizeObservers gate on it — a
 * callback delivered after teardown must never pin).
 */
export function useStickyPin(
  enabled: boolean,
  sentinelRef: RefObject<HTMLDivElement | null>,
  armedRef: RefObject<boolean>,
  { onEnter, onExit, onArm, onDisarm, pinBoundaryPx = 0 }: StickyPinCallbacks,
): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    if (!('IntersectionObserver' in window) || typeof window.matchMedia !== 'function') return
    // Armed on the SAME query the stylesheet gates the pin/morph rules with
    // (`@media (min-width: 933px)`) — not on the negation of the 932px mobile
    // query, whose open gap (932px, 933px) armed the hooks with no CSS active
    // (#1092 review).
    const mq = window.matchMedia(DESKTOP_MIN_QUERY)
    let observer: IntersectionObserver | null = null

    const setup = () => {
      if (observer || !mq.matches || !sentinelRef.current) return
      observer = new IntersectionObserver(
        (entries) => {
          // disconnect() removes targets but does NOT clear already-queued
          // notification tasks (IO processing model) — a callback delivered
          // after the mobile teardown must not commit a pin (#1092 r3).
          if (!armedRef.current) return
          const entry = entries[0]
          if (!entry) return
          // Pin only when the sentinel has scrolled ABOVE the boundary —
          // below-viewport non-intersection (bar not yet reached) must not
          // pin. The boundary is the viewport top plus the configured header
          // offset (see pinBoundaryPx above).
          if (!entry.isIntersecting && entry.boundingClientRect.top < pinBoundaryPx) {
            onEnter()
          } else if (entry.isIntersecting) {
            onExit()
          }
        },
        pinBoundaryPx > 0
          ? { threshold: 0, rootMargin: `${-pinBoundaryPx}px 0px 0px 0px` }
          : { threshold: 0 },
      )
      observer.observe(sentinelRef.current)
      armedRef.current = true
      onArm?.()
    }
    const teardown = () => {
      armedRef.current = false
      observer?.disconnect()
      observer = null
      onDisarm?.()
      onExit()
    }
    const onMqChange = () => (mq.matches ? setup() : teardown())

    setup()
    // Older embedded webviews only have the deprecated listener pair.
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onMqChange)
    else if (typeof mq.addListener === 'function') mq.addListener(onMqChange)
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', onMqChange)
      else if (typeof mq.removeListener === 'function') mq.removeListener(onMqChange)
      teardown()
    }
  }, [enabled, sentinelRef, armedRef, onEnter, onExit, onArm, onDisarm, pinBoundaryPx])
}

/**
 * The document-level Escape-collapses effect both hooks carry (module:
 * document keydown, searchbar-pro.js:5010-5014). The shapes diverged in
 * #1103 — the refine hook layers a guard (an Esc a popover consumed, or one
 * fired mid-interaction, closes the inner surface only) that the hero hook
 * deliberately does not have — so the guard is a parameter, not baked in.
 * `guard` must be referentially stable.
 */
export function useStickyEscape(
  active: boolean,
  onEscape: () => void,
  guard?: (e: KeyboardEvent) => boolean,
): void {
  useEffect(() => {
    if (!active) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (guard?.(e)) return
      onEscape()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [active, onEscape, guard])
}
